import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { logoUrlFor, localizedBranding } from "@/hooks/useBranding";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useI18n } from "@/i18n";
import {
  Palette,
  Upload,
  Save,
  Loader2,
  ArrowLeft,
  GraduationCap,
  Sparkles,
  Trash2,
  Lock,
  ShieldCheck,
  Eye,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORE_KEY = "coachscore_admin_pass";

const PRESET_COLORS = ["#6366F1", "#10B981", "#F59E0B", "#EC4899", "#0EA5E9", "#8B5CF6", "#EF4444", "#14B8A6"];

export default function SettingsPage() {
  const { t, lang } = useI18n();
  const [passcode, setPasscode] = useState("");
  const [authed, setAuthed] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const [form, setForm] = useState(null);
  const [fu, setFu] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const fileRef = useRef(null);

  const headers = { "X-Admin-Passcode": passcode };

  const verify = async (code) => {
    setVerifying(true);
    try {
      await axios.post(`${API}/admin/verify`, { passcode: code });
      localStorage.setItem(STORE_KEY, code);
      setPasscode(code);
      setAuthed(true);
    } catch (e) {
      toast.error(t("admin.invalid"));
      localStorage.removeItem(STORE_KEY);
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    const saved = localStorage.getItem(STORE_KEY);
    if (saved) verify(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!authed) return;
    axios.get(`${API}/branding`).then((r) => setForm(r.data)).catch(() => toast.error(t("set.saveFailed")));
    axios.get(`${API}/followups`, { headers }).then((r) => setFu(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setF = (k, v) => setFu((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/branding`, form, { headers });
      if (fu) await axios.put(`${API}/followups`, fu, { headers });
      toast.success(t("set.saved"));
    } catch (e) {
      toast.error(e?.response?.status === 401 ? t("set.unauthorized") : t("set.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/branding/logo`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      set("logo_path", data.path);
      toast.success(t("set.logoUploaded"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("set.logoFailed"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const loadPreview = async (type = "report") => {
    setPreviewLoading(true);
    try {
      const { data } = await axios.get(`${API}/email/preview`, { headers, params: { type, lang } });
      setPreviewHtml(data.html);
      setPreviewOpen(true);
    } catch (e) {
      toast.error(t("set.previewFailed"));
    } finally {
      setPreviewLoading(false);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0B0F17] text-slate-100 flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="flex justify-center mb-4">
            <LanguageToggle />
          </div>
          <div className="text-center mb-6">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-500/10 mb-4">
              <Lock size={26} className="text-indigo-400" />
            </span>
            <h1 className="font-heading text-2xl font-extrabold">{t("set.accessTitle")}</h1>
            <p className="text-sm text-slate-500 mt-1">{t("set.accessDesc")}</p>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              verify(passcode);
            }}
            className="space-y-3"
          >
            <Input
              type="password"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder={t("admin.passcodePh")}
              className="bg-[#111827] border-slate-800 text-slate-100 text-center"
              data-testid="settings-passcode-input"
              autoFocus
            />
            <Button
              type="submit"
              disabled={verifying || !passcode}
              className="w-full bg-indigo-600 hover:bg-indigo-500 text-white"
              data-testid="settings-unlock-btn"
            >
              {verifying ? (
                <>
                  <Loader2 size={16} className="animate-spin mr-2" /> {t("admin.verifying")}
                </>
              ) : (
                <>
                  <ShieldCheck size={16} className="mr-2" /> {t("admin.unlock")}
                </>
              )}
            </Button>
          </form>
          <div className="text-center mt-4">
            <Link to="/" className="text-xs text-slate-500 hover:text-slate-300">
              {t("set.backToApp")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen bg-[#0B0F17] flex items-center justify-center text-slate-500">
        <Loader2 className="animate-spin mr-2" /> {t("set.loading")}
      </div>
    );
  }

  const brandColor = form.brand_color || "#6366F1";
  const logoUrl = logoUrlFor(form.logo_path);

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-slate-900/80 border-b border-slate-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700">
              <Palette size={20} className="text-white" />
            </span>
            <div>
              <h1 className="font-heading text-lg font-bold leading-none">{t("set.title")}</h1>
              <p className="text-[11px] text-slate-500 leading-none mt-1">{t("set.subtitle")}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LanguageToggle />
            <Link to="/">
              <Button
                variant="outline"
                className="h-9 bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
                data-testid="settings-back-btn"
              >
                <ArrowLeft size={14} className="mr-1.5" /> {t("set.backToApp")}
              </Button>
            </Link>
            <Button
              onClick={save}
              disabled={saving}
              className="h-9 bg-indigo-600 hover:bg-indigo-500 text-white"
              data-testid="settings-save-btn"
            >
              {saving ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <Save size={15} className="mr-1.5" />}
              {t("set.save")}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <Card className="bg-[#111827] border-slate-800 p-6 space-y-5">
            <h2 className="font-heading text-base md:text-lg font-semibold">{t("set.identity")}</h2>

            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-400 mb-1.5 block">
                {t("set.brandName")}
              </Label>
              <Input
                value={form.brand_name || ""}
                onChange={(e) => set("brand_name", e.target.value)}
                className="bg-slate-900 border-slate-700 text-slate-100"
                data-testid="settings-brandname-input"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-400 mb-1.5 block">{t("set.logo")}</Label>
              <div className="flex items-center gap-3">
                <div
                  className={`h-14 w-32 rounded-lg border border-slate-700 flex items-center justify-center overflow-hidden ${
                    logoUrl ? "bg-white" : "bg-slate-900"
                  }`}
                >
                  {logoUrl ? (
                    <img src={logoUrl} alt="logo" className="max-h-12 max-w-[120px] object-contain" />
                  ) : (
                    <span className="text-xs text-slate-600">{t("set.noLogo")}</span>
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={onUpload}
                  className="hidden"
                  data-testid="settings-logo-input"
                />
                <Button
                  variant="outline"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
                  data-testid="settings-logo-upload-btn"
                >
                  {uploading ? (
                    <Loader2 size={14} className="animate-spin mr-1.5" />
                  ) : (
                    <Upload size={14} className="mr-1.5" />
                  )}
                  {t("set.upload")}
                </Button>
                {form.logo_path && (
                  <Button
                    variant="outline"
                    onClick={() => set("logo_path", "")}
                    className="bg-transparent border-slate-700 text-rose-400 hover:bg-rose-500/10 hover:border-rose-500/40"
                    data-testid="settings-logo-remove-btn"
                  >
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5">{t("set.logoHint")}</p>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-400 mb-1.5 block">
                {t("set.brandColor")}
              </Label>
              <div className="flex items-center gap-2 flex-wrap">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => set("brand_color", c)}
                    className={`h-8 w-8 rounded-full border-2 transition-transform duration-150 hover:scale-110 ${
                      brandColor.toLowerCase() === c.toLowerCase() ? "border-white" : "border-transparent"
                    }`}
                    style={{ background: c }}
                    data-testid={`settings-color-${c.replace("#", "")}`}
                    aria-label={c}
                  />
                ))}
                <input
                  type="color"
                  value={brandColor}
                  onChange={(e) => set("brand_color", e.target.value)}
                  className="h-8 w-10 rounded cursor-pointer bg-transparent border border-slate-700"
                  data-testid="settings-color-input"
                />
                <Input
                  value={brandColor}
                  onChange={(e) => set("brand_color", e.target.value)}
                  className="w-28 bg-slate-900 border-slate-700 text-slate-100 font-mono text-sm"
                />
              </div>
            </div>
          </Card>

          <Card className="bg-[#111827] border-slate-800 p-6 space-y-5">
            <h2 className="font-heading text-base md:text-lg font-semibold">{t("set.landingCopy")}</h2>
            <div className="space-y-3">
              <Label className="text-xs uppercase tracking-wide text-slate-400 block">{t("set.headline")}</Label>
              <LocalizedField
                labelEn={t("set.langEn")}
                labelTr={t("set.langTr")}
                valueEn={form.headline || ""}
                valueTr={form.headline_tr || ""}
                onEn={(v) => set("headline", v)}
                onTr={(v) => set("headline_tr", v)}
                rows={2}
                testidEn="settings-headline-input"
                testidTr="settings-headline-tr-input"
              />
            </div>
            <div className="space-y-3">
              <Label className="text-xs uppercase tracking-wide text-slate-400 block">{t("set.subheadline")}</Label>
              <LocalizedField
                labelEn={t("set.langEn")}
                labelTr={t("set.langTr")}
                valueEn={form.subheadline || ""}
                valueTr={form.subheadline_tr || ""}
                onEn={(v) => set("subheadline", v)}
                onTr={(v) => set("subheadline_tr", v)}
                rows={3}
                testidEn="settings-subheadline-input"
                testidTr="settings-subheadline-tr-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-400 mb-1.5 block">
                {t("set.introVideo")}
              </Label>
              <Input
                value={form.intro_video_url || ""}
                onChange={(e) => set("intro_video_url", e.target.value)}
                className="bg-slate-900 border-slate-700 text-slate-100"
                placeholder="https://www.youtube.com/watch?v=..."
                data-testid="settings-intro-video-input"
              />
              <p className="text-[11px] text-slate-500 mt-1.5">{t("set.introVideoHint")}</p>
            </div>
          </Card>

          <Card className="bg-[#111827] border-slate-800 p-6 space-y-4">
            <div className="flex items-center justify-between gap-2">
              <h2 className="font-heading text-base md:text-lg font-semibold">{t("set.reportEmail")}</h2>
              <Button
                variant="outline"
                onClick={() => loadPreview("report")}
                disabled={previewLoading}
                className="h-8 bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
                data-testid="settings-email-preview-btn"
              >
                {previewLoading ? (
                  <Loader2 size={14} className="animate-spin mr-1.5" />
                ) : (
                  <Eye size={14} className="mr-1.5" />
                )}
                {t("set.previewEmail")}
              </Button>
            </div>
            <div className="space-y-3">
              <Label className="text-xs uppercase tracking-wide text-slate-400 block">{t("set.intro")}</Label>
              <LocalizedField
                labelEn={t("set.langEn")}
                labelTr={t("set.langTr")}
                valueEn={form.email_intro || ""}
                valueTr={form.email_intro_tr || ""}
                onEn={(v) => set("email_intro", v)}
                onTr={(v) => set("email_intro_tr", v)}
                rows={3}
                testidEn="settings-email-intro-input"
                testidTr="settings-email-intro-tr-input"
              />
              <p className="text-[11px] text-slate-500">{t("set.introHint")}</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-400 mb-1.5 block">
                {t("set.bookingLink")}
              </Label>
              <Input
                value={form.booking_url || ""}
                onChange={(e) => set("booking_url", e.target.value)}
                className="bg-slate-900 border-slate-700 text-slate-100"
                placeholder="https://calendar.app.google/..."
                data-testid="settings-booking-input"
              />
              <p className="text-[11px] text-slate-500 mt-1.5">{t("set.bookingHint")}</p>
            </div>
          </Card>

          {fu && (
            <Card className="bg-[#111827] border-slate-800 p-6 space-y-5" data-testid="settings-followups">
              <h2 className="font-heading text-base md:text-lg font-semibold">{t("set.followups")}</h2>
              <label className="flex items-start gap-2.5 text-sm text-slate-300 cursor-pointer -mt-2">
                <input
                  type="checkbox"
                  checked={fu.enabled !== false}
                  onChange={(e) => setF("enabled", e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-indigo-500 shrink-0"
                  data-testid="followups-enabled-toggle"
                />
                {t("set.followupsToggle")}
              </label>
              <p className="text-[11px] text-slate-500 -mt-2">{t("set.followupsHint")}</p>

              {[
                { step: "day3", label: t("set.day3") },
                { step: "day7", label: t("set.day7") },
              ].map(({ step, label }) => (
                <div key={step} className="space-y-2.5 border-t border-slate-800 pt-4 first:border-0 first:pt-0">
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-widest font-semibold text-amber-400">{label}</span>
                    <Button
                      variant="outline"
                      onClick={() => loadPreview(step)}
                      disabled={previewLoading}
                      className="h-7 bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
                      data-testid={`followup-preview-${step}`}
                    >
                      <Eye size={13} className="mr-1.5" /> {t("set.preview")}
                    </Button>
                  </div>

                  <p className="text-[10px] uppercase tracking-widest text-slate-500">{t("set.langEn")}</p>
                  <Input
                    value={fu[`${step}_subject`] || ""}
                    onChange={(e) => setF(`${step}_subject`, e.target.value)}
                    className="bg-slate-900 border-slate-700 text-slate-100"
                    placeholder={t("set.subjectPh")}
                    data-testid={`followup-${step}-subject`}
                  />
                  <Textarea
                    value={fu[`${step}_body`] || ""}
                    onChange={(e) => setF(`${step}_body`, e.target.value)}
                    className="bg-slate-900 border-slate-700 text-slate-100"
                    rows={3}
                    placeholder={t("set.bodyPh")}
                    data-testid={`followup-${step}-body`}
                  />

                  <p className="text-[10px] uppercase tracking-widest text-slate-500">{t("set.langTr")}</p>
                  <Input
                    value={fu[`${step}_subject_tr`] || ""}
                    onChange={(e) => setF(`${step}_subject_tr`, e.target.value)}
                    className="bg-slate-900 border-slate-700 text-slate-100"
                    placeholder={t("set.subjectPh")}
                    data-testid={`followup-${step}-subject-tr`}
                  />
                  <Textarea
                    value={fu[`${step}_body_tr`] || ""}
                    onChange={(e) => setF(`${step}_body_tr`, e.target.value)}
                    className="bg-slate-900 border-slate-700 text-slate-100"
                    rows={3}
                    placeholder={t("set.bodyPh")}
                    data-testid={`followup-${step}-body-tr`}
                  />
                </div>
              ))}
            </Card>
          )}
        </div>

        {/* Live preview */}
        <div>
          <p className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-2.5">
            {t("set.livePreview")}
          </p>
          <Card className="bg-[#0B0F17] border-slate-800 p-6 sticky top-24" data-testid="settings-preview">
            <div className="flex items-center gap-3 mb-6">
              {logoUrl ? (
                <span className="inline-flex items-center rounded-lg bg-white px-2.5 py-1.5">
                  <img src={logoUrl} alt="logo" className="h-8 max-w-[150px] object-contain" />
                </span>
              ) : (
                <>
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: brandColor }}>
                    <GraduationCap size={18} className="text-white" />
                  </span>
                  <span className="font-heading font-bold">{form.brand_name || "CoachScore"}</span>
                </>
              )}
            </div>
            <span
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest mb-4"
              style={{ backgroundColor: `${brandColor}1a`, color: brandColor, border: `1px solid ${brandColor}4d` }}
            >
              <Sparkles size={12} /> {t("set.previewBadge")}
            </span>
            <h3 className="font-heading text-2xl font-extrabold leading-tight mb-3">
              {localizedBranding(form, "headline", lang) || t("set.previewHeadlinePh")}
            </h3>
            <p className="text-sm text-slate-400 leading-relaxed mb-5">
              {localizedBranding(form, "subheadline", lang) || t("set.previewSubPh")}
            </p>
            <Button style={{ backgroundColor: brandColor }} className="text-white hover:opacity-90">
              {t("set.previewCta")}
            </Button>
          </Card>
        </div>
      </main>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="bg-[#0B0F17] border-slate-800 text-slate-100 max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading text-base md:text-lg">{t("set.emailPreviewTitle")}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 rounded-lg overflow-hidden border border-slate-800 bg-white">
            <iframe
              title="email-preview"
              srcDoc={previewHtml}
              className="w-full h-[60vh] bg-white"
              sandbox=""
              data-testid="email-preview-frame"
            />
          </div>
          <p className="text-[11px] text-slate-500 mt-2">{t("set.emailPreviewNote")}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LocalizedField({ labelEn, labelTr, valueEn, valueTr, onEn, onTr, rows = 2, testidEn, testidTr }) {
  return (
    <div className="space-y-2">
      <div>
        <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{labelEn}</p>
        <Textarea
          value={valueEn}
          onChange={(e) => onEn(e.target.value)}
          className="bg-slate-900 border-slate-700 text-slate-100"
          rows={rows}
          data-testid={testidEn}
        />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{labelTr}</p>
        <Textarea
          value={valueTr}
          onChange={(e) => onTr(e.target.value)}
          className="bg-slate-900 border-slate-700 text-slate-100"
          rows={rows}
          data-testid={testidTr}
        />
      </div>
    </div>
  );
}
